import { useState, useEffect, useRef } from "react";
import { useAuthToken } from "@/contexts/OptimizedAuthContext";
import { extensionLogin, type ExtensionLoginResponse } from "@/services/api";
import { useAuthStatus } from "@/contexts/OptimizedAuthContext";

export function useExtensionAuth(client: string, client_version: string = "") {
  const getToken = useAuthToken();
  const { isSignedIn, isLoaded } = useAuthStatus();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExtensionLoginResponse | null>(null);
  // Used to avoid calling the login endpoint twice
  const hasRequested = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      // Prevent duplicate requests
      if (hasRequested.current) return;
      hasRequested.current = true;

      try {
        setLoading(true);
        const clerkToken = await getToken({ template: "specstory_cloud" });

        if (!clerkToken) {
          setError("Failed to get authentication token");
          return;
        }

        const response = await extensionLogin(
          clerkToken,
          client,
          client_version
        );
        setData(response);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to authenticate with extension"
        );
      } finally {
        setLoading(false);
      }
    };

    // Make sure we are not calling the login twice to avoid server errors
    // If the extension login endpoint is called 2 times from the same client in a short period of time,
    // the server trigger a duplicated key error for the second one since the key is based on time
    if (
      isLoaded &&
      isSignedIn &&
      client.length > 0 &&
      !error &&
      !data &&
      !hasRequested.current
    ) {
      void fetchData();
    }
  }, [error, data, getToken, client, client_version, isSignedIn, isLoaded]);

  return {
    loading,
    error,
    data,
  };
}
